import { LogicExpressionCondition } from '@doss/prisma/types'
import { DossDataFormat } from './types/table-cell.js'

/**
 * Operand type rules for logic-expression comparisons, in one place.
 *
 * These used to live inside the ANTLR model builder, so they only ran when a formula *string*
 * was parsed. Anything writing the structured tree directly — the GraphQL API, imports,
 * migrations, scripts — skipped them entirely. The rules now live here so both the parser
 * (for editor feedback) and `validateTableFormulaInput` (for every write path) apply the same set.
 */

/**
 * One side of a comparison, as the operand rules see it.
 *
 *  - `type` is the operand's resolved data format. `undefined` means the caller could not resolve
 *    it — a missing column, a form input, a literal of an unrecognized shape.
 *  - `isColumn` only picks the wording of the message ("Multi-select type column" vs "list").
 */
export interface LogicOperandType {
  type: DossDataFormat | undefined
  isColumn: boolean
}

/**
 * Which operand a violation belongs to. `expression` covers rules about the shape of the
 * comparison itself rather than either operand's type.
 */
export type LogicOperandSide = 'left' | 'right' | 'expression'

export interface LogicOperandViolation {
  side: LogicOperandSide
  message: string
}

const isArray = (operand: LogicOperandType | undefined): boolean =>
  operand?.type === DossDataFormat.ARRAY

const isBoolean = (operand: LogicOperandType | undefined): boolean =>
  operand?.type === DossDataFormat.BOOLEAN

const isStringOrEnum = (operand: LogicOperandType | undefined): boolean =>
  operand?.type === DossDataFormat.STRING || operand?.type === DossDataFormat.ENUM

const isArrayOrStringOrEnum = (operand: LogicOperandType | undefined): boolean =>
  isArray(operand) || isStringOrEnum(operand)

const listWord = (
  operand: LogicOperandType | undefined,
  columnWord: string,
  valueWord: string,
): string => (operand?.isColumn ? columnWord : valueWord)

/**
 * Checks a single comparison's operands against the rules for its condition.
 *
 * `left` / `right` are `undefined` when the comparison has no value in that position. A value
 * that is present but whose type could not be resolved is passed as `{ type: undefined }`;
 * such an operand fails the "must be" rules, so callers that cannot type an operand reliably
 * should skip the comparison instead of guessing.
 *
 * Returns every violation found, in left-then-right order. Callers decide what to do with them:
 * the parser turns them into editor error tokens, the write-path validator throws on the first.
 */
export const checkLogicExpressionOperandTypes = (
  condition: LogicExpressionCondition,
  left: LogicOperandType | undefined,
  right: LogicOperandType | undefined,
): LogicOperandViolation[] => {
  const violations: LogicOperandViolation[] = []

  switch (condition) {
    // If one value is an array, the other value must also be an array
    case LogicExpressionCondition.EQUAL:
    case LogicExpressionCondition.NOT_EQUAL: {
      // Only apply the error to the right value
      if (isArray(left) && !isArray(right)) {
        violations.push({
          side: 'right',
          message: `Right value has to be a ${listWord(right, 'Multi-select type column', 'list')}`,
        })
      }
      if (!isArray(left) && isArray(right)) {
        violations.push({
          side: 'right',
          message: `Right value cannot be a ${listWord(right, 'Multi-select type column', 'list')}`,
        })
      }
      break
    }

    // Neither value can be a boolean or an array
    case LogicExpressionCondition.GREATER_THAN:
    case LogicExpressionCondition.GREATER_THAN_OR_EQUAL:
    case LogicExpressionCondition.LESS_THAN:
    case LogicExpressionCondition.LESS_THAN_OR_EQUAL: {
      if (isBoolean(left)) {
        violations.push({
          side: 'left',
          message: `Left value cannot be a ${listWord(left, 'Boolean type column', 'boolean')}`,
        })
      }
      if (isBoolean(right)) {
        violations.push({
          side: 'right',
          message: `Right value cannot be a ${listWord(right, 'Boolean type column', 'boolean')}`,
        })
      }
      if (isArray(left)) {
        violations.push({
          side: 'left',
          message: `Left value cannot be a ${listWord(left, 'Multi-select type column', 'list')}`,
        })
      }
      if (isArray(right)) {
        violations.push({
          side: 'right',
          message: `Right value cannot be a ${listWord(right, 'Multi-select type column', 'list')}`,
        })
      }
      break
    }

    // Left can be array or string or enum, right cannot be array
    case LogicExpressionCondition.CONTAINS:
    case LogicExpressionCondition.DOES_NOT_CONTAIN: {
      if (!isArrayOrStringOrEnum(left)) {
        violations.push({
          side: 'left',
          message: `Left value must be a ${listWord(left, 'column of type Text, Select, or Multi-select', 'text or list input')}`,
        })
      }
      if (isArray(right)) {
        violations.push({
          side: 'right',
          message: `Right value cannot be a ${listWord(right, 'Multi-select type column', 'list input')}`,
        })
      }
      break
    }

    // Left has to be string or enum, right can be string or enum or array
    case LogicExpressionCondition.IN:
    case LogicExpressionCondition.NOT_IN: {
      if (!isStringOrEnum(left)) {
        violations.push({
          side: 'left',
          message: `Left value must be a ${listWord(left, 'Text or Select type column', 'text input')}`,
        })
      }
      if (!isArrayOrStringOrEnum(right)) {
        violations.push({
          side: 'right',
          message: `Right value must be a ${listWord(right, 'Text, Select, or Multi-select type column', 'text or list input')}`,
        })
      }
      break
    }

    // Both left and right have to be arrays
    case LogicExpressionCondition.DOES_NOT_CONTAIN_ANY:
    case LogicExpressionCondition.CONTAINS_ALL:
    case LogicExpressionCondition.CONTAINS_ANY: {
      if (!isArray(left)) {
        violations.push({
          side: 'left',
          message: `Left value must be a ${listWord(left, 'Multi-select type column', 'list input')}`,
        })
      }
      if (!isArray(right)) {
        violations.push({
          side: 'right',
          message: `Right value must be a ${listWord(right, 'Multi-select type column', 'list input')}`,
        })
      }
      break
    }

    // Invalid for expressions of type comparison
    case LogicExpressionCondition.IS_EMPTY:
    case LogicExpressionCondition.IS_NOT_EMPTY: {
      if (right) {
        violations.push({
          side: 'expression',
          message: `${condition} only checks if a field is empty - remove the second value`,
        })
      }
      if (!left) {
        violations.push({
          side: 'expression',
          message: `${condition} requires a field to check`,
        })
      }
      break
    }
  }

  return violations
}
